import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { DownloadSimple, Spinner } from "@phosphor-icons/react";
import { canWrite, usePortalIdentity } from "../auth/usePortalIdentity";
import {
  useActOnProposal,
  useClientOpenFaults,
  useClientReports,
  useReportDownload,
  monthWords,
  type ClientReport,
  type ClientReportProposal
} from "../data/reportQueries";
import { PORTAL_UNEXPECTED, toPortalFailure } from "../lib/errors";
import { BoxState } from "../ui/BoxState";
import { ClientHead } from "../ui/ClientHead";
import { Hint } from "../ui/Hint";
import { Panel } from "../ui/Panel";
import "./clientOps/clientOps.css";
import { useClientPath, useViewOnly } from "../data/readSource";

/**
 * Månadsrapporten, build round B2 (2026-10-09), decision 0515.
 *
 * The client reads Petrovic's monthly report here: the PDF, saved rather than shown inside the
 * portal, and its proposals, each with one button. "Gör det" on an included proposal and "Få ett
 * pris" on a priced one make one request each, which never counts toward the month's small changes.
 * A pressed proposal shows its request instead of the button, and a second press anywhere answers
 * with the same request.
 *
 * "Fel (under åtgärd)" is the faults open now, read the same way as Mercator's month read, and not a
 * figure frozen in the PDF: a fault fixed since the report was written is gone from it.
 *
 * Not a seventh destination (design.md section 5): Översikt's report box and the notice lead here, as
 * the notice for a secret leads to Hemligheter. `/reports` opens the newest report; `/reports/:month`
 * a given one.
 */

const dateOnly = new Intl.DateTimeFormat("sv-SE", { dateStyle: "long" });

function day(value: string): string {
  const parsed = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(parsed.getTime()) ? value : dateOnly.format(parsed);
}

/**
 * Build round 56, 2026-11-02: the press answers with the sentence its refusal code maps to, the daily
 * limit's included, and keeps its own sentence for a failure no code names. Until then every refusal
 * said "Försök igen", which is the wrong advice for a limit that lasts the day.
 */
const PROPOSAL_FAILED = "Det gick inte att skapa ärendet. Försök igen, och hör av er i chatten om det upprepas.";

function proposalRefusal(error: unknown): string {
  const failure = toPortalFailure(error);
  return failure.code === PORTAL_UNEXPECTED ? PROPOSAL_FAILED : failure.message;
}

function Proposal({
  proposal,
  tenantId,
  writable
}: {
  proposal: ClientReportProposal;
  tenantId: string;
  writable: boolean;
}) {
  const act = useActOnProposal();
  const viewOnly = useViewOnly();
  const clientPath = useClientPath();
  const priced = proposal.kind === "priced";
  const label = priced ? "Få ett pris" : "Gör det";

  return (
    <li className="k-proposal" data-testid="report-proposal" data-kind={proposal.kind}>
      <div className="k-proposal-text">
        <strong>{proposal.title}</strong>
        <p className="k-proposal-why">{proposal.why}</p>
        <p className="quiet">{priced ? "Kostar extra. Ni får ett pris innan något görs." : "Ingår i förvaltningen och räknas inte mot månadens ändringar."}</p>
      </div>
      <div className="k-proposal-act">
        {proposal.request_number !== null ? (
          <Link to={clientPath(`/requests/${proposal.request_number}`)} data-testid="report-proposal-request">
            Ärende {proposal.request_number}
          </Link>
        ) : writable ? (
          // "Visa som kund" (build round 41): the client's button is absent, not disabled.
          viewOnly ? null : (
          <button
            type="button"
            className="button is-primary"
            data-testid="report-proposal-act"
            disabled={act.isPending}
            onClick={() => act.mutate({ tenantId, proposalId: proposal.id })}
          >
            {act.isPending ? <Spinner size={16} aria-hidden="true" /> : null}
            <span>{label}</span>
          </button>
          )
        ) : (
          <span className="quiet" data-testid="report-proposal-readonly">
            Portalen är skrivskyddad just nu.
          </span>
        )}
        {act.isError ? (
          <p className="form-error" role="alert" data-testid="report-proposal-error">
            {proposalRefusal(act.error)}
          </p>
        ) : null}
      </div>
    </li>
  );
}

function Faults({ tenantId }: { tenantId: string }) {
  const faults = useClientOpenFaults(tenantId);

  return (
    <Panel title="Fel (under åtgärd)" titleId="report-faults" hint="De fel som är öppna nu, inte när rapporten skrevs." testId="report-faults">
      {faults.isLoading ? (
        <BoxState kind="loading" compact title="Hämtar felen" />
      ) : faults.isError ? (
        <BoxState kind="error" compact title="Felen gick inte att hämta" onRetry={() => void faults.refetch()} />
      ) : (faults.data ?? []).length === 0 ? (
        <p className="quiet" data-testid="report-faults-none">
          Inga fel är öppna just nu.
        </p>
      ) : (
        <ul className="k-rows">
          {(faults.data ?? []).map((fault) => (
            <li key={fault.fault_id} data-testid="report-fault">
              <strong>{fault.title}</strong> <span className="quiet">sedan {day(fault.since)}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function Report({ report, tenantId, writable }: { report: ClientReport; tenantId: string; writable: boolean }) {
  const download = useReportDownload();
  const viewOnly = useViewOnly();

  return (
    <div className="stack" data-testid="report-view" data-month={report.month}>
      <Panel title={`Rapporten för ${monthWords(report.month)}`} titleId="report-file" testId="report-file">
        <p className="quiet">Publicerad {day(report.published_at)}.</p>
        {viewOnly ? null : (
        <button
          type="button"
          className="button"
          data-testid="report-download"
          disabled={download.isPending}
          onClick={() => download.mutate({ tenantId, reportId: report.report_id, month: report.month })}
        >
          <DownloadSimple size={16} aria-hidden="true" />
          <span>Ladda ner rapporten (PDF)</span>
        </button>
        )}
        {download.isError ? (
          <p className="form-error" role="alert" data-testid="report-download-error">
            Rapporten gick inte att hämta. Försök igen.
          </p>
        ) : null}
      </Panel>

      <Panel title="Förslag" titleId="report-proposals" testId="report-proposals">
        {report.proposals.length === 0 ? (
          <p className="quiet" data-testid="report-no-proposals">
            Inga förslag den här månaden.
          </p>
        ) : (
          <ul className="k-rows">
            {report.proposals.map((proposal) => (
              <Proposal key={proposal.id} proposal={proposal} tenantId={tenantId} writable={writable} />
            ))}
          </ul>
        )}
      </Panel>

      <Faults tenantId={tenantId} />
    </div>
  );
}

export function ReportsRoute() {
  const clientPath = useClientPath();
  const { month } = useParams();
  const identity = usePortalIdentity();
  const tenantId = identity.data?.tenantId ?? null;
  const reports = useClientReports(tenantId);

  const page = (content: ReactNode) => (
    <div className="k-page">
      <ClientHead
        title="Månadsrapporter"
        actions={<Hint>Rapporten kommer den femte varje månad. De tolv senaste finns kvar här.</Hint>}
      />
      {content}
    </div>
  );
  const stateBox = (state: ReactNode) => <section className="panel">{state}</section>;

  if (identity.isLoading || reports.isLoading) {
    return page(stateBox(<BoxState kind="loading" title="Hämtar rapporterna" />));
  }

  if (!tenantId) {
    return page(stateBox(<BoxState kind="denied" title="Den här vyn är inte din" body="Kontot hör inte till någon kund i portalen." />));
  }

  if (reports.isError) {
    return page(
      stateBox(
        <BoxState
          kind="error"
          title="Rapporterna gick inte att hämta"
          body="Inget har ändrats. Försök igen, och hör av er i chatten om det upprepas."
          onRetry={() => void reports.refetch()}
        />
      )
    );
  }

  const all = reports.data ?? [];

  if (all.length === 0) {
    return page(stateBox(<BoxState kind="empty" title="Ingen rapport än" body="Den första rapporten kommer den femte i månaden efter att förvaltningen börjat." />));
  }

  const shown = month ? all.find((report) => report.month === month) : all[0];

  if (!shown) {
    return page(stateBox(<BoxState kind="empty" title="Den månaden har ingen rapport" body="Välj en av månaderna nedan." />));
  }

  const others = all.filter((report) => report.report_id !== shown.report_id);

  return page(
    <div className="stack">
      <Report report={shown} tenantId={tenantId} writable={canWrite(identity.data)} />
      {others.length > 0 ? (
        <Panel title="Tidigare rapporter" titleId="report-earlier" testId="report-earlier" flush>
          <ul className="k-rows">
            {others.map((report) => (
              <li key={report.report_id}>
                <Link to={clientPath(`/reports/${report.month}`)} data-testid="report-earlier-link">
                  {monthWords(report.month)}
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
    </div>
  );
}
